import axios from 'axios';

// Cookie-based auth: relace je v httpOnly cookie (server ji nastaví při loginu).
// withCredentials posílá cookie; CSRF token čteme z non-httpOnly cookie a posíláme v hlavičce.
const api = axios.create({ baseURL: '/api', withCredentials: true });

function getCookie(name) {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

// Double-submit CSRF: u mutačních metod přidej X-CSRF-Token z cookie.
api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (!['get', 'head', 'options'].includes(method)) {
    const csrf = getCookie('eccka_csrf');
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

// Při 401 (vypršelá relace) přesměruj na přihlášení – mimo veřejné stránky.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const path = window.location.pathname;
    const publicPaths = ['/login', '/register'];
    if (err.response?.status === 401 && !publicPaths.includes(path)) {
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (email, password) => api.post('/auth/login', { email, password }),
  logout: () => api.post('/auth/logout'),
  verify: () => api.get('/auth/verify'),
  changePassword: (current, newPass) => api.put('/auth/password', { current_password: current, new_password: newPass }),
  updateSkills: (skills) => api.put('/auth/skills', { skills }),
  updateProfile: (data) => api.put('/auth/profile', data),
  members: () => api.get('/auth/members'),
  setRole: (id, role, chapterId) => api.patch(`/auth/members/${id}/role`, { role, chapter_id: chapterId }),
  deleteMember: (id) => api.delete(`/auth/members/${id}`),
};

// Schopnosti/znalosti člena – klíče musí odpovídat MEMBER_SKILLS v backendu (database.js).
export const MEMBER_SKILLS = [
  { key: 'PSP', label: 'PSP', desc: 'Psychosociální pomoc' },
  { key: 'KI', label: 'KI', desc: 'Krizová intervence' },
  { key: 'Technik', label: 'Technik', desc: 'Technické a materiální zajištění' },
  { key: 'OpS', label: 'OpS', desc: 'Operační středisko / řízení' },
  { key: 'Gastro', label: 'Gastro', desc: 'Stravování a gastro provoz' },
  { key: 'Ubytovací', label: 'Ubytovací', desc: 'Ubytování a nouzové přístřeší' },
];

export const chaptersApi = {
  list: () => api.get('/chapters'),
  get: (id) => api.get(`/chapters/${id}`),
  create: (data) => api.post('/chapters', data),
  update: (id, data) => api.put(`/chapters/${id}`, data),
  regenerateCode: (id) => api.post(`/chapters/${id}/regenerate-code`),
  delete: (id) => api.delete(`/chapters/${id}`),
};

export const catalogApi = {
  tree: () => api.get('/catalog/tree'),
  categories: () => api.get('/catalog/categories'),
  subcategories: (catId) => api.get(`/catalog/categories/${catId}/subcategories`),
  itemTypes: (subId) => api.get(`/catalog/subcategories/${subId}/item-types`),
  itemType: (id) => api.get(`/catalog/item-types/${id}`),
  createCategory: (data) => api.post('/catalog/categories', data),
  updateCategory: (id, data) => api.put(`/catalog/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/catalog/categories/${id}`),
  createSubcategory: (data) => api.post('/catalog/subcategories', data),
  updateSubcategory: (id, data) => api.put(`/catalog/subcategories/${id}`, data),
  deleteSubcategory: (id) => api.delete(`/catalog/subcategories/${id}`),
  createItemType: (data) => api.post('/catalog/item-types', data),
  updateItemType: (id, data) => api.put(`/catalog/item-types/${id}`, data),
  deleteItemType: (id) => api.delete(`/catalog/item-types/${id}`),
};

export const inventoryApi = {
  list: (chapterId) => api.get('/inventory', { params: chapterId ? { chapter_id: chapterId } : {} }),
  search: (params) => api.get('/inventory/search', { params }),
  create: (data) => api.post('/inventory', data),
  update: (id, data) => api.put(`/inventory/${id}`, data),
  setQuantity: (id, quantity) => api.patch(`/inventory/${id}/quantity`, { quantity }),
  delete: (id) => api.delete(`/inventory/${id}`),
  csvUrl: (chapterId) => `/api/inventory/export.csv${chapterId ? `?chapter_id=${encodeURIComponent(chapterId)}` : ''}`,
};

export const auditApi = {
  list: (params) => api.get('/audit', { params: params || {} }),
  actions: () => api.get('/audit/actions'),
  csvUrl: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return `/api/audit/csv${q ? `?${q}` : ''}`;
  },
};

export const eventsApi = {
  list: () => api.get('/events'),
  get: (id) => api.get(`/events/${id}`),
  create: (data) => api.post('/events', data),
  update: (id, data) => api.put(`/events/${id}`, data),
  delete: (id) => api.delete(`/events/${id}`),
  participants: (id) => api.get(`/events/${id}/participants`),
  participantsCsvUrl: (id) => `/api/events/${id}/participants/csv`,
  timeline: (id) => api.get(`/events/${id}/timeline`),
  timelineCsvUrl: (id) => `/api/events/${id}/timeline.csv`,
  materialOverview: (id) => api.get(`/events/${id}/material-overview`),
  materialCsvUrl: (id) => `/api/events/${id}/material-overview.csv`,
  // Mapa MU (v2.15)
  map: (id) => api.get(`/events/${id}/map`),
  setMapView: (id, data) => api.patch(`/events/${id}/map-view`, data),
  createPoi: (id, data) => api.post(`/events/${id}/pois`, data),
  updatePoi: (id, poiId, data) => api.put(`/events/${id}/pois/${poiId}`, data),
  deletePoi: (id, poiId) => api.delete(`/events/${id}/pois/${poiId}`),
};

// Mapa MU – palety pro body zájmu (POI).
export const POI_ICONS = ['🏳️', '➕', '⛺', '🅿️', '💧', '📦', '⚠️', '🚁', '➡️', '👥', '🔥', '⭐', 'ℹ️', '📍'];
export const POI_COLORS = [
  { key: 'red', hex: '#e02424' },
  { key: 'blue', hex: '#2563eb' },
  { key: 'green', hex: '#16a34a' },
  { key: 'orange', hex: '#ea580c' },
  { key: 'purple', hex: '#7c3aed' },
  { key: 'gray', hex: '#64748b' },
];
export const poiColorHex = (key) => (POI_COLORS.find((c) => c.key === key) || POI_COLORS[5]).hex;

export const zonesApi = {
  list: (eventId) => api.get(`/events/${eventId}/zones`),
  get: (zoneId) => api.get(`/zones/${zoneId}`),
  create: (eventId, name, parentZoneId = null) =>
    api.post(`/events/${eventId}/zones`, { name, parent_zone_id: parentZoneId }),
  rename: (zoneId, name) => api.patch(`/zones/${zoneId}/name`, { name }),
  setParent: (zoneId, parentZoneId) => api.patch(`/zones/${zoneId}/parent`, { parent_zone_id: parentZoneId || null }),
  delete: (zoneId) => api.delete(`/zones/${zoneId}`),
  history: (zoneId, eventId) => api.get(`/zones/${zoneId}/history?event_id=${eventId}`),
  qrUrl: (zoneId) => `/api/zones/${zoneId}/qr`,
  qrPreviewUrl: (zoneId) => `/api/zones/${zoneId}/qr-preview`,
  qrSinglePrintUrl: (zoneId) => `/api/zones/${zoneId}/qr-single-print`,
  setGeometry: (zoneId, geometry, color) => api.patch(`/zones/${zoneId}/geometry`, { geometry, color }),
};

// Mapa MU – paleta barev zón (hex se ukládá přímo do zones.color).
export const ZONE_COLORS = ['#e02424', '#2563eb', '#16a34a', '#ea580c', '#7c3aed', '#0891b2'];

export const vehiclesApi = {
  list: (chapterId) => api.get('/vehicles', { params: chapterId ? { chapter_id: chapterId } : {} }),
  get: (id) => api.get(`/vehicles/${id}`),
  create: (data) => api.post('/vehicles', data),
  update: (id, data) => api.put(`/vehicles/${id}`, data),
  delete: (id) => api.delete(`/vehicles/${id}`),
};

export const tripsApi = {
  list: (params) => api.get('/trips', { params: params || {} }),
  mine: () => api.get('/trips/mine'),
  get: (id) => api.get(`/trips/${id}`),
  overview: () => api.get('/trips/overview'),
  drivers: (chapterId) => api.get('/trips/drivers', { params: chapterId ? { chapter_id: chapterId } : {} }),
  create: (data) => api.post('/trips', data),
  update: (id, data) => api.put(`/trips/${id}`, data),
  setStatus: (id, status, fuelEnd) => api.patch(`/trips/${id}/status`, { status, ...(fuelEnd != null ? { fuel_end: fuelEnd } : {}) }),
  delete: (id) => api.delete(`/trips/${id}`),
  // jednotky konvoje
  units: (id) => api.get(`/trips/${id}/vehicles`),
  addUnit: (id, data) => api.post(`/trips/${id}/vehicles`, data),
  updateUnit: (id, tvId, data) => api.put(`/trips/${id}/vehicles/${tvId}`, data),
  deleteUnit: (id, tvId) => api.delete(`/trips/${id}/vehicles/${tvId}`),
  // náklad
  cargo: (id) => api.get(`/trips/${id}/cargo`),
  addCargo: (id, data) => api.post(`/trips/${id}/cargo`, data),
  updateCargo: (id, cargoId, data) => api.put(`/trips/${id}/cargo/${cargoId}`, data),
  deleteCargo: (id, cargoId) => api.delete(`/trips/${id}/cargo/${cargoId}`),
  // vyúčtování (odpisy)
  settlement: (id) => api.get(`/trips/${id}/settlement`),
  settle: (id, data) => api.post(`/trips/${id}/settle`, data),
  csvUrl: (params) => {
    const q = new URLSearchParams(params || {}).toString();
    return `/api/trips/export.csv${q ? `?${q}` : ''}`;
  },
  cargoCsvUrl: (id) => `/api/trips/${id}/cargo.csv`,
};

export const containersApi = {
  list: (chapterId) => api.get('/containers', { params: chapterId ? { chapter_id: chapterId } : {} }),
  get: (id) => api.get(`/containers/${id}`),
  create: (data) => api.post('/containers', data),
  update: (id, data) => api.put(`/containers/${id}`, data),
  delete: (id) => api.delete(`/containers/${id}`),
  items: (id) => api.get(`/containers/${id}/items`),
  addItem: (id, data) => api.post(`/containers/${id}/items`, data),
  updateItem: (id, itemId, data) => api.put(`/containers/${id}/items/${itemId}`, data),
  deleteItem: (id, itemId) => api.delete(`/containers/${id}/items/${itemId}`),
};

export const writeoffsApi = {
  list: (chapterId, eventId) => api.get('/writeoffs', { params: { ...(chapterId ? { chapter_id: chapterId } : {}), ...(eventId ? { event_id: eventId } : {}) } }),
  csvUrl: (chapterId, eventId) => `/api/writeoffs/export.csv?${new URLSearchParams({ ...(chapterId ? { chapter_id: chapterId } : {}), ...(eventId ? { event_id: eventId } : {}) }).toString()}`,
};

export const WRITEOFF_REASONS = { consumed: 'Spotřebováno', damaged: 'Poškozeno', lost: 'Ztraceno', donated: 'Darováno' };

export const FEEDBACK_TYPES = {
  bug: '🐞 Chyba – něco nefunguje',
  idea: '💡 Návrh / nápad',
  question: '❓ Dotaz',
  confusing: '😕 Něco je matoucí',
  other: '➕ Jiné',
};

export const feedbackApi = {
  submit: (data) => api.post('/feedback', data),
  list: (params) => api.get('/feedback', { params: params || {} }),
  setStatus: (id, status) => api.patch(`/feedback/${id}`, { status }),
  csvUrl: (params) => `/api/feedback/export.csv${params && Object.keys(params).length ? '?' + new URLSearchParams(params).toString() : ''}`,
};

export const handoverApi = {
  list: (eventId) => api.get('/handover', { params: { event_id: eventId } }),
  create: (data) => api.post('/handover', data),
  delete: (id) => api.delete(`/handover/${id}`),
  rotateToken: (id) => api.post(`/handover/${id}/rotate-token`),
  qrUrl: (id) => `/api/handover/${id}/qr`,
  scan: (slug) => api.get(`/handover/scan/${slug}`),
  confirm: (slug, data) => api.post(`/handover/scan/${slug}/confirm`, data),
  checkpointEvents: (id) => api.get(`/handover/${id}/events`),
  dashboard: (eventId) => api.get(`/handover/dashboard/${eventId}`),
  setLocation: (id, lat, lng) => api.patch(`/handover/${id}/location`, { lat, lng }),
  tripHandoverEvents: (tripId) => api.get(`/trips/${tripId}/handover`),
  csvUrl: (eventId) => `/api/handover/export.csv?event_id=${encodeURIComponent(eventId)}`,
};

export const checkinApi = {
  perform: (data) => api.post('/checkin', data),
  status: (eventId, zoneId) =>
    api.get('/checkin/status', { params: { event_id: eventId, ...(zoneId ? { zone_id: zoneId } : {}) } }),
  active: () => api.get('/checkin/active'),
  history: (eventId) => api.get('/checkin/history', { params: eventId ? { event_id: eventId } : {} }),
  adminCheckin: (data) => api.post('/checkin/admin', data),
};

// Rozpozná typ naskenovaného QR a vrátí kam směřuje:
//  - předání materiálu:  { kind: 'handover', slug, token }   (URL /q/h/:slug?t=…)
//  - vstup do zóny:      { kind: 'zone', slug }               (URL /q/:slug)
//                        { kind: 'zone', event, zone }        (URL /checkin?event=&zone=)
// Ruční zadání (holý text) bereme jako slug zóny.
export function parseScan(text) {
  const raw = (text || '').trim();
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.origin);
    const parts = url.pathname.split('/').filter(Boolean);
    // Předání: /q/h/:slug
    if (parts[0] === 'q' && parts[1] === 'h' && parts[2]) {
      return { kind: 'handover', slug: parts[2], token: url.searchParams.get('t') || '' };
    }
    // Zóna (krátké URL): /q/:slug
    if (parts[0] === 'q' && parts[1] && !parts[2]) {
      return { kind: 'zone', slug: parts[1] };
    }
    // Zóna (fallback URL): /checkin?event=&zone=
    const event = url.searchParams.get('event');
    const zone = url.searchParams.get('zone');
    if (event && zone) return { kind: 'zone', event, zone };
  } catch { /* není URL – ber jako slug zóny */ }
  return { kind: 'zone', slug: raw };
}

// Přesměruje podle rozpoznaného QR. navigate = z react-router; pro zónové slug
// použijeme tvrdou navigaci (server /q/:slug přesměruje na check-in).
export function routeScan(parsed, navigate) {
  if (!parsed) return false;
  if (parsed.kind === 'handover') {
    navigate(`/q/h/${parsed.slug}${parsed.token ? `?t=${encodeURIComponent(parsed.token)}` : ''}`);
    return true;
  }
  if (parsed.kind === 'zone') {
    if (parsed.event && parsed.zone) navigate(`/checkin?event=${parsed.event}&zone=${parsed.zone}`);
    else if (parsed.slug) window.location.href = `/q/${parsed.slug}`;
    else return false;
    return true;
  }
  return false;
}

export default api;
